import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@antimatterhq/ui/lib/utils';

const tooltipVariants = cva(
  [
    'z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs',
    'max-w-[350px]',
    'animate-in fade-in-0 zoom-in-95',
    'data-[side=bottom]:slide-in-from-top-2',
    'data-[side=left]:slide-in-from-right-2',
    'data-[side=right]:slide-in-from-left-2',
    'data-[side=top]:slide-in-from-bottom-2',
    '[&_a]:underline [&_a]:opacity-90 [&_a:hover]:opacity-100',
  ],
  {
    variants: {
      variant: {
        default: 'bg-grey-800 text-white dark:bg-grey-100 dark:text-grey-800',
        success: 'bg-green-600 text-white dark:bg-green-500',
        error: 'bg-red-600 text-white dark:bg-red-500',
        warning: 'bg-yellow-600 text-white dark:bg-yellow-500',
        info: 'bg-blue-600 text-white dark:bg-blue-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type TooltipProps = {
  content?: React.ReactNode | string;
  children?: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
  spanClassName?: string;
  style?: React.CSSProperties;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
} & VariantProps<typeof tooltipVariants> &
  Pick<
    TooltipPrimitive.TooltipProps,
    'delayDuration' | 'disableHoverableContent'
  >;

export default function Tooltip({
  content,
  children,
  side = 'right',
  align = 'center',
  variant,
  className,
  spanClassName,
  style,
  delayDuration,
  open,
  onOpenChange,
  disableHoverableContent,
}: TooltipProps) {
  return (
    <TooltipPrimitive.Root
      delayDuration={delayDuration}
      open={open}
      onOpenChange={onOpenChange}
      disableHoverableContent={disableHoverableContent}
    >
      <TooltipPrimitive.Trigger asChild className={className}>
        <span className={cn(spanClassName)} style={style}>
          {children}
        </span>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={4}
          className={cn(tooltipVariants({ variant }))}
        >
          {content}

          <TooltipPrimitive.Arrow
            className={cn(
              (!variant || variant === 'default') &&
                'fill-grey-800 dark:fill-grey-100',
              variant === 'success' && 'fill-green-600 dark:fill-green-500',
              variant === 'error' && 'fill-red-600 dark:fill-red-500',
              variant === 'warning' && 'fill-yellow-600 dark:fill-yellow-500',
              variant === 'info' && 'fill-blue-600 dark:fill-blue-500',
            )}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const TooltipProvider = TooltipPrimitive.Provider;
